// ═══════════════════════════════════════════════════
//  Client-side Seat Cushion Cost Calculation
//  Mirrors server-side helpers for live cost preview.
//  Sibling to back-cushion-cost.ts.
// ═══════════════════════════════════════════════════

import {
  type MaterialLibrary,
  type FabricPackingResult,
  type FabricPatternDirection,
  type Piece,
  type ProductInputs,
  type SlotInputs,
  type UpholsteryDesignAllowances,
  type DesignTraitSource,
  generateFabricPieces,
  packFabricPieces,
  resolveDesignTypeAllowancePct,
  resolveDesignTraits,
  resolveWireLinearFeet,
  computeDesignBearingFraction,
  type ComponentNestingPart,
  collectFinishablePanels,
  computeWoodFinish,
  type FinishRate,
  type WoodFinishRow,
  type Finish,
  componentDims,
  resolveFramingDims,
  resolveFoamLayers,
  resolveChannelCount,
  resolveChannelWidthWarning,
  resolveButtonCount,
  resolveSpringCount,
  resolveButtonHardware,
  resolveWireHardware,
  priceMaterialByUnit,
  type MaterialUnitPrice,
  type ChargedUnitLabel,
  type UnitPricedPiece,
} from '@estimator/shared';

/** Currency context from project settings for MXN → USD conversion */
export interface CurrencyContext {
  mxn_exchange_rate: number | null;
  mxn_bank_spread: number;
}

/** Project context for cost mode + currency */
export interface ProjectCostContext extends CurrencyContext {
  nesting_cost_overrides: Record<string, string>;
}

function coercePatternDirection(raw: unknown): FabricPatternDirection {
  if (raw === 'railroad' || raw === 'up_the_bolt') return raw;
  return 'none';
}

// Material detail types
export interface BanquetteMaterialDetail {
  material_id: string;
  material_name: string;
  total_sqft: number;
  sqft_with_waste: number;
  cost: number;
  /** What was billed and in which unit, for the breakdown label. */
  charged_qty?: number;
  charged_unit?: ChargedUnitLabel;
  num_sheets?: number;
  sheet_size?: string;
  currency_note?: string;
}

export interface BanquetteSlotDetail {
  slot_id: string;
  name: string;
  calc_mode: string;
  real_inches: number;
  sqft: number;
  cost: number;
  material_name?: string;
  num_sheets?: number;
  currency_note?: string;
  material_id?: string;
  material_category?: string;
  material_subtype?: string | null;
  fabric_pieces?: Array<{
    panel_type: 'front' | 'side' | 'back';
    section_index: number;
    panel_index?: number;
    width_in: number;
    length_in: number;
    design_up_axis: 'width' | 'length';
  }>;
  waste_factor_used?: number;
  sheet_size?: string;
  note?: string;
}

export interface SeatCushionHardwarePreviewDetail {
  name: string;
  qty: number;
  cost_per_unit: number;
  cost: number;
  unit?: string;
  currency_note?: string;
}

export interface SeatCushionCostPreviewResult {
  framing_cost: number;
  foam_cost: number;
  dacron_cost: number;
  fabric_cost: number;
  finish_cost: number;
  /** Applied wood finish on framing panels — distinct from finish_cost, which
   *  is drawn-slot surface material. */
  wood_finish_cost: number;
  wood_finish_details: WoodFinishRow[];
  hardware_cost: number;
  /** Why a button charge came out at zero, when it did. Empty is normal. */
  hardware_warnings: string[];
  total_cost: number;
  total_cost_excluding_fabric: number;
  rate_per_lf: number;
  rib_count: number;
  spring_count: number;
  channel_count_per_section: number;
  button_count: number;
  clip_count: number;
  framing_materials: BanquetteMaterialDetail[];
  foam_materials: BanquetteMaterialDetail[];
  slot_details: BanquetteSlotDetail[];
  hardware_items: SeatCushionHardwarePreviewDetail[];
  fabric_packings: FabricPackingResult[];
  /** Sheet-goods rectangles for the nesting preview. Fabric and dacron excluded — neither is nested. */
  nesting_parts: ComponentNestingPart[];
}

// ── Currency conversion helpers ──

function convertMaterialCost(
  mat: MaterialLibrary,
  currencyCtx?: CurrencyContext,
): { cost_per_unit: number; original_cost_mxn?: number; currency_note?: string } {
  let costPerUnit = mat.cost_per_unit ?? 0;
  const currency = (mat as any).currency || 'USD';
  const ivaPercent = (mat as any).iva_percent || 0;

  if (ivaPercent > 0) {
    costPerUnit = costPerUnit * (1 + ivaPercent / 100);
  }

  if (currency === 'MXN' && currencyCtx?.mxn_exchange_rate) {
    const effectiveRate = currencyCtx.mxn_exchange_rate - (currencyCtx.mxn_bank_spread || 0);
    if (effectiveRate > 0) {
      const originalMxn = costPerUnit;
      costPerUnit = costPerUnit / effectiveRate;
      const note = `MXN $${originalMxn.toFixed(2)} ÷ ${effectiveRate.toFixed(4)} = $${costPerUnit.toFixed(4)} USD`;
      return { cost_per_unit: costPerUnit, original_cost_mxn: originalMxn, currency_note: note };
    }
  }

  return { cost_per_unit: costPerUnit };
}

function convertHardwareCost(
  hw: { cost_per_unit: number; name: string; unit?: string; currency?: string; iva_percent?: number },
  currencyCtx?: CurrencyContext,
): { cost_per_unit: number; currency_note?: string } {
  let costPerUnit = hw.cost_per_unit ?? 0;
  const currency = (hw as any).currency || 'USD';
  const ivaPercent = (hw as any).iva_percent || 0;

  if (ivaPercent > 0) {
    costPerUnit = costPerUnit * (1 + ivaPercent / 100);
  }

  if (currency === 'MXN' && currencyCtx?.mxn_exchange_rate) {
    const effectiveRate = currencyCtx.mxn_exchange_rate - (currencyCtx.mxn_bank_spread || 0);
    if (effectiveRate > 0) {
      const originalMxn = costPerUnit;
      costPerUnit = costPerUnit / effectiveRate;
      const note = `MXN $${originalMxn.toFixed(2)} ÷ ${effectiveRate.toFixed(4)} = $${costPerUnit.toFixed(4)} USD`;
      return { cost_per_unit: costPerUnit, currency_note: note };
    }
  }

  return { cost_per_unit: costPerUnit };
}

// ── Pure helpers ──

function resolveRibCount(
  framing: { rib_mode?: string; rib_spacing?: number; rib_count?: number },
  lfQty: number,
): number {
  if (framing.rib_mode === 'manual') return Math.max(0, Math.floor(framing.rib_count ?? 0));
  const spacing = framing.rib_spacing ?? 24;
  if (spacing <= 0) return 0;
  return Math.max(0, Math.floor((lfQty * 12) / spacing) - 1);
}

/** Options a caller can add to computeSheetCost beyond area and waste. */
interface ComputeSheetCostOptions {
  /**
   * How many discrete pieces this material is cut into. Only an `each`
   * material needs it — area carries no piece count — but passing it is
   * harmless for every other unit.
   */
  pieceQty?: number;
  /**
   * The rectangles cut from this material. Only linear-foot pricing reads
   * them, to rip whole boards instead of dividing a pooled area by the board
   * width. Omitting them falls back to the area model.
   */
  pieces?: readonly UnitPricedPiece[];
  /**
   * Sink for "could not price this" warnings. Pass the same array the caller
   * folds into `hardware_warnings`; duplicates are dropped, since one
   * misconfigured material is usually priced several times in a run.
   */
  warnings?: string[];
  /**
   * Filled in with the full pricing result. Lets the caller label a breakdown
   * row with the quantity actually billed — `4.4 lf` rather than the square
   * footage every row used to print regardless of unit.
   */
  result?: { price?: MaterialUnitPrice };
}

/**
 * Numeric mirror of the server's computeSheetCost. The unit semantics live in
 * `priceMaterialByUnit` in @estimator/shared, so this side and the server side
 * cannot drift the way they did while each compared `material.unit` against
 * its own `'sq ft'` / `'lin ft'` literals — literals that matched neither of
 * the `square_foot` / `linear_foot` spellings the Materials Library writes.
 *
 * Note the extra `convertedCostPerUnit` in position 4, which the server
 * signature does not have: this side prices in the display currency.
 */
function computeSheetCost(
  totalSqft: number,
  material: MaterialLibrary | null,
  wasteFactor: number,
  convertedCostPerUnit?: number,
  costMode: 'whole_sheet' | 'used_area' = 'whole_sheet',
  opts: ComputeSheetCostOptions = {},
): [number, number, number] {
  if (!material) return [0, 0, 0];

  const priced = priceMaterialByUnit(
    totalSqft,
    { ...material, cost_per_unit: convertedCostPerUnit ?? material.cost_per_unit ?? 0 },
    wasteFactor,
    { costMode, pieceQty: opts.pieceQty, pieces: opts.pieces, materialName: material.name },
  );

  if (opts.warnings) {
    for (const w of priced.warnings) {
      if (!opts.warnings.includes(w)) opts.warnings.push(w);
    }
  }

  if (opts.result) opts.result.price = priced;

  return [priced.num_sheets, priced.cost, priced.sheet_sqft];
}

// ── Cushion-shared helpers ──
// All three count resolvers now live in @estimator/shared. Re-exported because
// SeatCushionModal imports them from this module.

export { resolveChannelCount, resolveButtonCount, resolveSpringCount };

// ── Main cost preview ──

export function calcSeatCushionCostPreview(
  config: any,
  materials: Map<string, MaterialLibrary>,
  hardware: Map<string, { cost_per_unit: number; name: string; unit?: string; currency?: string; iva_percent?: number }>,
  pageScalePpi?: number,
  projectCtx?: ProjectCostContext,
  upholsteryAllowances?: UpholsteryDesignAllowances | null,
  opts?: {
    forceCostMode?: 'used_area';
    finishes?: Finish[];
    /** Catalog rows, for their behavior traits. Optional: without them
     *  resolveDesignTraits falls back to the seeded map, which reproduces
     *  this function's previous literal comparisons for the eight shipped
     *  designs. */
    designCatalog?: readonly DesignTraitSource[];
  },
  /** Button hardware to use when the design names none. Mirrors the server
   *  helper's parameter of the same name — see spec 2026-09-02 R3. */
  defaultButtonHardwareId?: string | null,
  /** Tufting wire hardware to use when the design names none. Mirrors the
   *  parameter of the same name on the other seven helpers. See spec D4. */
  defaultWireHardwareId?: string | null,
): SeatCushionCostPreviewResult {
  const lfQty = config.lf_quantity ?? 0;
  const profileHeight = config.profile_height ?? 4;
  const profileDepth = config.profile_depth ?? 20;
  const sectionCount = Math.max(1, Math.floor(config.section_count ?? 1));
  const sectionWidthInches = lfQty > 0 ? (lfQty * 12) / sectionCount : 0;
  const framing = resolveFramingDims(config.framing ?? {}, componentDims(config));
  const baseFramingWaste = framing.waste_factor ?? 0.10;
  const curvedR = Math.max(0, Math.floor(framing.curved_section_count ?? 0));
  const curvedExtra = Math.max(0, framing.curved_section_waste_factor ?? 0);
  const curvedFraction = sectionCount > 0 ? Math.min(1, curvedR / sectionCount) : 0;
  const effectiveFramingWaste = baseFramingWaste + curvedExtra * curvedFraction;
  const design = config.design ?? { type: 'plain' };
  const springs = config.springs ?? {};
  const substrateMode = config.substrate_mode ?? 'full_frame';

  let framingCost = 0;
  let foamCost = 0;
  let dacronCost = 0;
  let finishCost = 0;
  let fabricCost = 0;
  let hardwareCost = 0;
  const hardwareWarnings: string[] = [];

  const framingMaterials: BanquetteMaterialDetail[] = [];
  const foamMaterials: BanquetteMaterialDetail[] = [];
  const slotDetails: BanquetteSlotDetail[] = [];
  const hardwareItems: SeatCushionHardwarePreviewDetail[] = [];

  const matConversions = new Map<string, { cost_per_unit: number; currency_note?: string; costMode: 'whole_sheet' | 'used_area' }>();
  for (const [id, mat] of materials) {
    const conv = convertMaterialCost(mat, projectCtx);
    // Same precedence as the server (SeatCushionCostService.buildMaterialMap):
    // forced mode → project override → material default → whole sheet.
    const costMode = (
      opts?.forceCostMode ??
      projectCtx?.nesting_cost_overrides?.[id] ??
      (mat as any).nesting_cost_mode ??
      'whole_sheet'
    ) as 'whole_sheet' | 'used_area';
    matConversions.set(id, { ...conv, costMode });
  }
  const hwConversions = new Map<string, { cost_per_unit: number; currency_note?: string }>();
  for (const [id, hw] of hardware) {
    hwConversions.set(id, convertHardwareCost(hw, projectCtx));
  }

  // ── Framing ──
  const nestingParts: ComponentNestingPart[] = [];
  /**
   * "Could not price this material" warnings, e.g. a linear-foot material with
   * no board width. Collected separately from hardwareWarnings so the
   * documented button -> channel -> wire order survives, then appended.
   */
  const materialWarnings: string[] = [];
  const framingByMat = new Map<
    string,
    /**
     * totalQty counts discrete pieces; only `each` pricing reads it.
     * runPieces holds the rectangles themselves, which only linear-foot
     * pricing reads, to rip whole boards instead of dividing a pooled area.
     */
    { mat: MaterialLibrary; totalSqft: number; totalQty: number; runPieces: UnitPricedPiece[] }
  >();

  /**
   * Record one rectangle. Area feeds the cost roll-up; the geometry feeds the
   * nesting preview. canRotate mirrors the server part-collector.
   */
  function addPiece(
    name: string,
    widthIn: number,
    heightIn: number,
    qty: number,
    matId: string | null,
    wasteFactor: number,
    canRotate: boolean,
    /** Run a linear-foot board along the piece's shorter dimension. */
    boardRunFlipped = false,
  ) {
    if (!matId || widthIn <= 0 || heightIn <= 0 || qty <= 0) return;
    const mat = materials.get(matId);
    if (!mat) return;
    const sqft = (widthIn * heightIn * qty) / 144;
    const runPiece: UnitPricedPiece = {
      width_in: widthIn, length_in: heightIn, qty, flipped: boardRunFlipped,
    };
    const existing = framingByMat.get(matId);
    if (existing) {
      existing.totalSqft += sqft;
      existing.totalQty += qty;
      existing.runPieces.push(runPiece);
    } else {
      framingByMat.set(matId, { mat, totalSqft: sqft, totalQty: qty, runPieces: [runPiece] });
    }
    nestingParts.push({
      name,
      width_in: widthIn,
      height_in: heightIn,
      qty,
      material_id: matId,
      waste_factor: wasteFactor,
      can_rotate: canRotate,
    });
  }

  if (substrateMode === 'full_frame' || substrateMode === 'both') {
    addPiece('Side Panel', profileDepth, profileHeight, 2 * sectionCount, framing.side_panel_material_id, effectiveFramingWaste, true);
    const ribCount = resolveRibCount(framing, lfQty);
    if (ribCount > 0) addPiece('Rib', profileDepth, profileHeight, ribCount, framing.rib_material_id, effectiveFramingWaste, true);
    for (const rail of framing.rails ?? []) {
      addPiece(rail.name ?? 'Rail', rail.width ?? 0, rail.length ?? 0, 1, rail.material_id, effectiveFramingWaste, false, rail.board_run_flipped ?? false);
    }
    for (const panel of framing.additional_panels ?? []) {
      const qty = (panel.qty_per_section ?? 1) * sectionCount;
      addPiece(panel.name ?? 'Panel', panel.width ?? 0, panel.height ?? 0, qty, panel.material_id, effectiveFramingWaste, true, panel.board_run_flipped ?? false);
    }
  }
  if (substrateMode === 'simple_backer' || substrateMode === 'both') {
    const backer = config.backer_panel;
    if (backer?.material_id) {
      addPiece('Backer Panel', sectionWidthInches, profileHeight, sectionCount, backer.material_id, effectiveFramingWaste, true);
    }
  }

  for (const [matId, g] of framingByMat) {
    const conv = matConversions.get(matId);
    const priceOut: { price?: MaterialUnitPrice } = {};
    const [ns, c] = computeSheetCost(g.totalSqft, g.mat, effectiveFramingWaste, conv?.cost_per_unit, conv?.costMode,
      { pieceQty: g.totalQty, pieces: g.runPieces, warnings: materialWarnings, result: priceOut });
    framingCost += c;
    framingMaterials.push({
      material_id: matId,
      material_name: g.mat.name,
      total_sqft: g.totalSqft,
      sqft_with_waste: g.totalSqft * (1 + effectiveFramingWaste),
      cost: c,
      charged_qty: priceOut.price?.charged_qty,
      charged_unit: priceOut.price?.charged_unit,
      num_sheets: ns > 0 ? ns : undefined,
      currency_note: conv?.currency_note,
    });
  }

  // ── Foam Layers ──
  // Mirrors seat-cushion-cost.helpers.ts. curvedFraction is already computed
  // above for the framing waste.
  const foamWaste = (config.foam_waste_factor ?? 0.10)
    + Math.max(0, config.foam_curved_waste_factor ?? 0) * curvedFraction;
  const dacronWaste = (config.dacron_waste_factor ?? 0.10)
    + Math.max(0, config.dacron_curved_waste_factor ?? 0) * curvedFraction;
  const foamByMat = new Map<
    string,
    /** totalQty counts discrete pieces; only `each` pricing reads it. */
    { mat: MaterialLibrary; totalSqft: number; totalQty: number }
  >();

  // Mirrors seat-cushion-cost.helpers.ts: resolved against the component's live
  // dimensions so this preview and the saved server estimate agree.
  const resolvedFoam = resolveFoamLayers(config.foam_layers ?? [], componentDims(config));

  for (const layer of resolvedFoam) {
    const layerWidth = layer.width;
    if (!layer.material_id || layerWidth <= 0) continue;
    const mat = materials.get(layer.material_id);
    if (!mat) continue;

    // A null length means Auto — the layer runs the full LF length.
    const layerLengthIn = layer.length;
    const layerSqft = (layerWidth * layerLengthIn) / 144;

    const existing = foamByMat.get(layer.material_id);
    if (existing) {
      existing.totalSqft += layerSqft;
      existing.totalQty += 1;
    } else {
      foamByMat.set(layer.material_id, { mat, totalSqft: layerSqft, totalQty: 1 });
    }
    nestingParts.push({
      name: layer.name ?? 'Foam',
      width_in: layerWidth,
      height_in: layerLengthIn,
      qty: 1,
      material_id: layer.material_id,
      waste_factor: foamWaste,
      can_rotate: false,
    });
  }

  for (const [matId, g] of foamByMat) {
    const conv = matConversions.get(matId);
    const [ns, c] = computeSheetCost(g.totalSqft, g.mat, foamWaste, conv?.cost_per_unit, conv?.costMode,
      { pieceQty: g.totalQty, warnings: materialWarnings });
    foamCost += c;
    foamMaterials.push({
      material_id: matId,
      material_name: g.mat.name,
      total_sqft: g.totalSqft,
      sqft_with_waste: g.totalSqft * (1 + foamWaste),
      cost: c,
      num_sheets: ns > 0 ? ns : undefined,
      currency_note: conv?.currency_note,
    });
  }

  // ── Dacron ──
  const dacronMat = config.dacron_material_id ? materials.get(config.dacron_material_id) : undefined;
  if (dacronMat && lfQty > 0) {
    const dacronConv = config.dacron_material_id ? matConversions.get(config.dacron_material_id) : undefined;
    const dacronSqft = (lfQty * 12 * profileHeight) / 144;
    const [, c] = computeSheetCost(dacronSqft, dacronMat as any, dacronWaste, dacronConv?.cost_per_unit, dacronConv?.costMode,
      { pieceQty: 1, warnings: materialWarnings });
    dacronCost = c;
  }

  // ── Slots ──
  const ppi = pageScalePpi ?? config.image_scale_ppi ?? 0;
  const sectionLfIn = sectionCount > 0 ? (lfQty / sectionCount) * 12 : 0;
  const productInputs: ProductInputs = {
    product_type: 'banquette_seat_cushion',
    section_count: sectionCount,
    section_lf_in: sectionLfIn,
    profile_height_in: profileHeight,
    profile_depth_in: profileDepth,
  };

  const piecesByMaterial = new Map<
    string,
    {
      mat: MaterialLibrary;
      pieces: Piece[];
      slotDetails: BanquetteSlotDetail[];
    }
  >();

  for (const slot of config.slots ?? []) {
    const slotId: string = slot.id ?? '';
    const mat = materials.get(slot.material_id);
    if (!mat) {
      slotDetails.push({
        slot_id: slotId,
        name: slot.name ?? '',
        calc_mode: 'linear',
        real_inches: 0,
        sqft: 0,
        cost: 0,
        note: 'no material',
      });
      continue;
    }
    if (ppi <= 0) {
      slotDetails.push({
        slot_id: slotId,
        name: slot.name ?? '',
        calc_mode: 'linear',
        real_inches: 0,
        sqft: 0,
        cost: 0,
        note: 'scale not set',
        material_id: mat.id,
        material_name: mat.name,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
      });
      continue;
    }
    const slotConv = matConversions.get(slot.material_id);
    let totalPx = 0;
    for (const p of slot.polylines ?? []) totalPx += p.length_px ?? 0;
    const realIn = totalPx / ppi;

    const isFabric = !!(mat as any).fabric_subtype || ((mat as any).material_type ?? '').toLowerCase() === 'fabric';

    if (isFabric) {
      const slotInputs: SlotInputs = {
        slot_id: slotId,
        slot_name: slot.name ?? '',
        panel_role: slot.panel_role ?? 'front',
        polyline_length_in: realIn,
        staple_allowance_in: slot.fabric_staple_allowance ?? 1,
        fabric_wrap_value: slot.fabric_wrap_value ?? null,
      };
      const pieces = generateFabricPieces(slotInputs, productInputs);
      const rawAreaIn2 = pieces.reduce((s, p) => s + p.width_in * p.length_in, 0);

      const slotInfo: BanquetteSlotDetail = {
        slot_id: slotId,
        name: slot.name ?? '',
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: realIn,
        sqft: rawAreaIn2 / 144,
        cost: 0,
        material_name: mat.name,
        currency_note: slotConv?.currency_note,
        material_id: mat.id,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
        fabric_pieces: pieces.map((p) => ({
          panel_type: p.panel_type,
          section_index: p.section_index,
          panel_index: p.panel_index,
          width_in: p.width_in,
          length_in: p.length_in,
          design_up_axis: p.design_up_axis,
        })),
      };
      slotDetails.push(slotInfo);

      let group = piecesByMaterial.get(mat.id);
      if (!group) {
        group = { mat, pieces: [], slotDetails: [] };
        piecesByMaterial.set(mat.id, group);
      }
      group.pieces.push(...pieces);
      group.slotDetails.push(slotInfo);
    } else {
      const sqft = (realIn * lfQty * 12) / 144;
      const [numSheets, c] = computeSheetCost(sqft, mat, baseFramingWaste, slotConv?.cost_per_unit, slotConv?.costMode,
        { pieceQty: sqft > 0 ? 1 : 0, warnings: materialWarnings });
      finishCost += c;
      slotDetails.push({
        slot_id: slotId,
        name: slot.name ?? '',
        calc_mode: slot.calc_mode ?? 'linear',
        real_inches: realIn,
        sqft,
        cost: c,
        material_name: mat.name,
        currency_note: slotConv?.currency_note,
        material_id: mat.id,
        material_category: (mat as any).category,
        material_subtype: (mat as any).fabric_subtype ?? null,
        waste_factor_used: baseFramingWaste,
        num_sheets: numSheets > 0 ? numSheets : undefined,
        sheet_size: (mat.unit === 'sheet' || mat.unit === 'roll') ? `${mat.sheet_width ?? 48}x${mat.sheet_length ?? 96}` : undefined,
      });
    }
  }

  // Catalog traits for this design. Resolved once and reused everywhere below
  // that used to test design.type against string literals: the fabric
  // allowance chain here, the button/channel counts, and the tufting wire
  // branch.
  const traits = resolveDesignTraits(design.type, opts?.designCatalog);

  // ── Per-material fabric packing + cost apportionment ──
  const fabricPackings: FabricPackingResult[] = [];
  for (const [matId, group] of piecesByMaterial) {
    const rollWidth = (group.mat as any).roll_width ?? 54;
    const patternDirection = coercePatternDirection((group.mat as any).pattern_direction);
    const result = packFabricPieces(
      group.pieces,
      { width_in: rollWidth, max_length_in: 1080 },
      patternDirection,
    );

    let designPct: number;
    if ((config as any).fabric_design_pct != null) {
      designPct = (config as any).fabric_design_pct;
    } else {
      const channelsPerSection = resolveChannelCount(design, sectionWidthInches, profileHeight, traits);
      const totalButtons = resolveButtonCount(design, lfQty, traits);
      const buttonsPerSection = totalButtons / Math.max(1, sectionCount);
      const designTypePct = resolveDesignTypeAllowancePct(
        design.type ?? 'plain',
        upholsteryAllowances,
        channelsPerSection,
        buttonsPerSection,
        traits,
      );
      designPct = designTypePct != null
        ? designTypePct
        : ((group.mat as any).fabric_design_pct ?? 0);
    }
    const patternAllowancePct = (config as any).fabric_pattern_allowance_pct ?? 0;
    const matWasteFactor = group.mat.waste_factor ?? 0;

    const conv = matConversions.get(matId);
    const costPerUnit = conv?.cost_per_unit ?? group.mat.cost_per_unit ?? 0;

    const designBearingFraction = computeDesignBearingFraction(group.pieces);
    const effectiveDesignPct = designPct * designBearingFraction;

    const finalLengthIn =
      result.total_length_in *
      (1 + effectiveDesignPct + patternAllowancePct + matWasteFactor);
    const finalYards = finalLengthIn / 36;
    const cost = finalYards * costPerUnit;
    fabricCost += cost;

    const contributingSlotIds = Array.from(
      new Set(group.slotDetails.map((s) => s.slot_id).filter((id): id is string => !!id)),
    );

    fabricPackings.push({
      material_id: matId,
      material_name: group.mat.name,
      roll_width_in: rollWidth,
      pattern_direction: patternDirection,
      rolls: result.rolls,
      total_rolls: result.total_rolls,
      total_length_in: result.total_length_in,
      final_length_in: finalLengthIn,
      final_yards: finalYards,
      contributing_slot_ids: contributingSlotIds,
      design_pct: designPct,
      effective_design_pct: effectiveDesignPct,
      pattern_allowance_pct: patternAllowancePct,
      material_waste_factor: matWasteFactor,
      cost,
      currency_note: conv?.currency_note,
      warnings: result.warnings,
    });

    const totalArea = group.pieces.reduce(
      (s, p) => s + p.width_in * p.length_in,
      0,
    );
    for (const slotDetail of group.slotDetails) {
      const slotArea = (slotDetail.fabric_pieces ?? []).reduce(
        (s, p) => s + p.width_in * p.length_in,
        0,
      );
      slotDetail.cost = totalArea > 0 ? cost * (slotArea / totalArea) : 0;
      if (result.warnings.length > 0) {
        slotDetail.note = [slotDetail.note, result.warnings.join('; ')]
          .filter(Boolean)
          .join(' | ');
      }
    }
  }

  // ── Hardware ──
  const channelCount = resolveChannelCount(design, sectionWidthInches, profileHeight, traits);
  const buttonCount = resolveButtonCount(design, lfQty, traits);
  const springsEnabled = !!springs.springs_enabled;
  const springCount = springsEnabled ? resolveSpringCount(springs, lfQty) : 0;
  const connectingWireCount = springs.connecting_wire_count ?? 1;
  const clipCount = (springCount * 2) + (springCount * connectingWireCount);

  function addHw(hwId: string | null, qty: number, unit?: string) {
    if (!hwId || qty <= 0) return;
    const hw = hardware.get(hwId);
    if (!hw) return;
    const conv = hwConversions.get(hwId);
    const effectiveCost = conv?.cost_per_unit ?? hw.cost_per_unit;
    const cost = qty * effectiveCost;
    hardwareCost += cost;
    hardwareItems.push({ name: hw.name, qty, cost_per_unit: effectiveCost, cost, unit, currency_note: conv?.currency_note });
  }

  // Buttons do not go through addHw: the design→default fallback and the three
  // silent-zero warnings live in one shared function so the server helper and
  // this one cannot drift again. See spec 2026-09-02 D1.
  const button = resolveButtonHardware(
    buttonCount,
    design.button_hardware_id,
    defaultButtonHardwareId,
    (id) => {
      const hw = hardware.get(id);
      if (!hw) return undefined;
      // The converted rate, so the warning and the row agree with what the
      // rest of this function charges.
      return { cost_per_unit: hwConversions.get(id)?.cost_per_unit ?? hw.cost_per_unit, name: hw.name };
    },
  );
  if (button.warning) hardwareWarnings.push(button.warning);
  if (button.name) {
    hardwareCost += button.cost;
    hardwareItems.push({
      name: button.name,
      qty: buttonCount,
      cost_per_unit: button.cost_per_unit,
      cost: button.cost,
      unit: 'each',
      currency_note: hwConversions.get(button.hardware_id!)?.currency_note,
    });
  }
  // The channel warning is pushed HERE, after the button one, and not up beside
  // resolveChannelCount where it is computed from. hardware_warnings reaches the
  // user as ordered prose — the modal preview joins it, and so does the server's
  // `note` — so the eight mirrored helpers have to agree on the order, not just
  // the contents. Button-then-channel is the order all four server helpers use.
  const channelWarning = resolveChannelWidthWarning(design, traits);
  if (channelWarning) hardwareWarnings.push(channelWarning);

  // Tufting wire. Which designs carry it, and which axis it runs along, are
  // catalog traits now — a design an admin marks as tufted earns wire here
  // with no code change.
  const wireLf = resolveWireLinearFeet(
    channelCount, traits, sectionWidthInches, profileHeight, sectionCount,
  );
  const wire = resolveWireHardware(
    wireLf,
    design.wire_hardware_id,
    defaultWireHardwareId,
    (id) => {
      const hw = hardware.get(id);
      if (!hw) return undefined;
      // The converted rate, so the warning and the row agree with what the
      // rest of this function charges.
      return { cost_per_unit: hwConversions.get(id)?.cost_per_unit ?? hw.cost_per_unit, name: hw.name };
    },
  );
  if (wire.warning) hardwareWarnings.push(wire.warning);
  hardwareWarnings.push(...materialWarnings);
  if (wire.name) {
    hardwareCost += wire.cost;
    hardwareItems.push({
      name: wire.name,
      qty: wireLf,
      cost_per_unit: wire.cost_per_unit,
      cost: wire.cost,
      unit: 'lin ft',
      currency_note: hwConversions.get(wire.hardware_id!)?.currency_note,
    });
  }
  if (springsEnabled) {
    addHw(springs.spring_hardware_id, springCount, 'each');
    addHw(springs.clip_hardware_id, clipCount, 'each');
    addHw(springs.connecting_wire_hardware_id, connectingWireCount * lfQty, 'lin ft');
  }

  // Applied wood finish. Net area only — no waste factor.
  const finishMap = new Map<string, FinishRate>(
    (opts?.finishes ?? []).map((f) => [
      f.id,
      { id: f.id, name: f.name, cost_per_sqft: Number(f.cost_per_sqft ?? 0) },
    ]),
  );
  const panelSqftEachForFinish = (profileDepth * profileHeight) / 144;
  const woodFinish = computeWoodFinish(
    collectFinishablePanels(framing, {
      sectionCount,
      ribCount: resolveRibCount(framing, lfQty),
      sidePanelSqftEach: panelSqftEachForFinish,
      ribSqftEach: panelSqftEachForFinish,
      railRunLengthIn: sectionCount > 0 ? (lfQty * 12) / sectionCount : 0,
    }),
    (framing as any).default_finish_id,
    finishMap,
  );

  const totalCost = framingCost + foamCost + dacronCost + finishCost + fabricCost + hardwareCost + woodFinish.cost;
  const totalCostExcludingFabric = totalCost - fabricCost;

  return {
    framing_cost: framingCost,
    foam_cost: foamCost,
    dacron_cost: dacronCost,
    fabric_cost: fabricCost,
    finish_cost: finishCost,
    wood_finish_cost: woodFinish.cost,
    wood_finish_details: woodFinish.rows,
    hardware_cost: hardwareCost,
    hardware_warnings: hardwareWarnings,
    total_cost: totalCost,
    total_cost_excluding_fabric: totalCostExcludingFabric,
    rate_per_lf: lfQty > 0 ? totalCost / lfQty : 0,
    rib_count: resolveRibCount(framing, lfQty),
    spring_count: springCount,
    channel_count_per_section: channelCount,
    button_count: buttonCount,
    clip_count: clipCount,
    framing_materials: framingMaterials,
    foam_materials: foamMaterials,
    slot_details: slotDetails,
    hardware_items: hardwareItems,
    fabric_packings: fabricPackings,
    nesting_parts: nestingParts,
  };
}
